/**
 * Bank payment QR for the registration page: scan it from another device or
 * save it to this one ("Guardar QR") and pay from the banking app's gallery picker.
 */

import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PaymentQrCardProps {
  url: string;
  /** Used for the downloaded file name, e.g. the event code */
  fileLabel: string;
}

const EXTENSIONS: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };

export function PaymentQrCard({ url, fileLabel }: PaymentQrCardProps) {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const save = async () => {
    setSaving(true);
    setFailed(false);
    try {
      // <a download> is ignored for cross-origin URLs → fetch the image and save the blob
      const res = await fetch(url);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const ext = EXTENSIONS[blob.type] ?? 'png';
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `QR-pago-${fileLabel}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      // Fallback: open the image so it can be saved manually (long-press on phones)
      setFailed(true);
      window.open(url, '_blank', 'noopener');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 rounded-md border bg-muted/30 p-4">
      <img src={url} alt="QR para pagar la inscripción" className="size-56 max-w-full rounded bg-white object-contain p-2" />
      <Button type="button" variant="outline" onClick={save} disabled={saving}>
        {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Download className="mr-2 size-4" />}
        Guardar QR
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        {failed
          ? 'Se abrió la imagen en otra pestaña: manténgala presionada para guardarla.'
          : 'Escanéelo desde otro dispositivo o guárdelo para pagar desde su app del banco.'}
      </p>
    </div>
  );
}
